import { HttpPromise } from "@shared/svelte/Modules/ApiService";
import LocalStorageCache from "@shared/services/local_storage_cache";
import type { PricingDataResult } from "@shared/types/Pricing";
import type { InventoryPrice } from "@shared/types/ProductLine";

// TODO this is set to 5 minutes for initial release, but should be set to 1 hour
const cache = new LocalStorageCache("pricingData", 5 * 60 * 1000); // Expires in an hour

export const getInventoryIdsFromSearchResponse = searchResponse => {
  const inventoryIds = [];

  searchResponse.forEach(productLine => {
    if (!productLine.products) return;

    productLine.products.forEach(product => {
      if (!product.inventory) return;

      product.inventory.forEach(inventory => {
        inventoryIds.push(inventory.id);
      });
    });
  });

  return inventoryIds;
};

export const getPricesForSearchResult = async (searchResponse): Promise<InventoryPrice[]> => {
  const inventoryIds = getInventoryIdsFromSearchResponse(searchResponse);

  if (inventoryIds.length === 0) return [];
  const pricingData: PricingDataResult[] = await getDataForInventories(inventoryIds);
  return pricingData.map(p => p.inventory_price).flat();
};

export const getDataForInventories = async (
  inventoryIds: string[],
): Promise<PricingDataResult[]> => {
  const fetchFunction = async (inventoryIds: string[]) => {
    let result = await HttpPromise({
      method: "POST",
      url: "/api/retailer/inventory/pricing_data",
      data: {
        inventory_ids: inventoryIds,
      },
    });
    return result.reduce((acc, r) => ({ ...acc, [r.inventory_id]: r }), {});
  };
  return cache.getMany(inventoryIds, fetchFunction) as Promise<PricingDataResult[]>;
};

export const getDataForInventory = async (
  inventoryId: string | number,
  errorCb?,
  warehouseLocationCode?: string | null,
): Promise<PricingDataResult> => {
  const fetchFunction = async (inventoryId: string) => {
    try {
      const data = { inventory_ids: [inventoryId] };
      if (warehouseLocationCode) {
        data["warehouse_location_code"] = warehouseLocationCode.startsWith("VIP_")
          ? warehouseLocationCode.substring(4)
          : warehouseLocationCode;
      }
      let results = await HttpPromise({
        method: "POST",
        url: "/api/retailer/inventory/pricing_data",
        data,
      });

      return results[0];
    } catch (e) {
      if (errorCb) {
        errorCb(e);
      } else {
        return {} as Promise<PricingDataResult>;
      }
    }
  };
  return cache.get(inventoryId.toString(), fetchFunction) as Promise<PricingDataResult>;
};

export const clearCacheForInventory = (inventoryId: string) => cache.remove(inventoryId);
